import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {SigninProvider} from 'auth/AuthConstants';
import {FONT_WEIGHT_PROXIMA_SEMIBOLD, LEGACY_FONT_PROXIMA_15} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {ButtonLink, ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY, GRAY_LIGHTER, NEAR_BLACK, WHITE} from 'ui-library/Colors';
import {Optional} from 'util/Types';

interface SigninChooseProviderPageProps {
  providers: Array<SigninProvider>;
  nextURL: Optional<string>;
}

function getSigninURL(idpName: string, nextURL: Optional<string>): string {
  const url = `/ext/saml/signin:begin?idp=${encodeURIComponent(idpName)}`;
  return nextURL != null ? `${url}&next=${nextURL}` : url;
}

export default function SigninChooseProviderPage({
  providers,
  nextURL,
}: SigninChooseProviderPageProps): JSX.Element {
  return (
    <div
      className={css(styles.page)}
      {...getTestProps({component: 'SigninChooseProviderPage', element: 'root'})}
    >
      <div className={css(styles.signinContainer)}>
        <img className={css(styles.benchlingLogo)} src="/static/img/navy_wordmark_500.png" />
        <div className={css(styles.instructions)}>Choose a single sign-on provider</div>
        {providers.map(({name, logoURL}, i) => (
          <React.Fragment key={name}>
            <ButtonLink
              className={css(styles.providerButton)}
              textClassName={css(styles.buttonContentWrapper)}
              href={getSigninURL(name, nextURL)}
              buttonStyle={ButtonStyle.TERTIARY_WHITE}
              size={ButtonSize.LARGE}
              fullWidth={true}
              text={
                <div className={css(styles.buttonContent)}>
                  <img className={css(styles.providerLogo)} src={logoURL} alt={name} />
                  <span className={css(styles.signInPrompt)}>Sign in with {name}</span>
                </div>
              }
              dataTestAttrs={{component: 'SigninChooseProviderPage', element: 'ButtonLink', key: name}}
            />
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  page: {
    alignItems: 'center',
    backgroundColor: GRAY_LIGHTER,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  signinContainer: {
    alignItems: 'center',
    backgroundColor: WHITE,
    borderColor: GRAY,
    borderRadius: 3,
    borderStyle: 'solid',
    borderWidth: 1,
    display: 'flex',
    flexDirection: 'column',
    marginTop: 80,
    padding: 40,
    width: 400,
  },
  benchlingLogo: {
    height: 42,
    width: 200,
  },
  instructions: {
    ...LEGACY_FONT_PROXIMA_15,
    margin: 16,
  },
  // These buttons are special, so override some of their styles to break the normal button rules.
  providerButton: {
    margin: 8,
    padding: 4,
  },
  buttonContentWrapper: {
    width: '100%',
  },
  buttonContent: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    height: 40,
  },
  providerLogo: {
    flex: 1,
    height: '80%',
    objectFit: 'contain',
  },
  signInPrompt: {
    ...LEGACY_FONT_PROXIMA_15,
    color: NEAR_BLACK,
    flex: 3,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    textAlign: 'center',
  },
});
